import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './buyJGKToken.css';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import { CircularProgress } from '@material-ui/core';
import { ViewPortContext } from '../../context/ViewPortContext';
import {
  getLocalOpsConfig,
  formatJGK,
  formatUsdJgkPairFromJgk,
} from '../../utils/localOpsConfig';
import { COLLISION_SPECIALIST_RIDE_TEMPLATE } from '../../utils/localOpsCollisionSpecialistRide';

const PRESET_AMOUNTS = [5, 10, 20, 50, 100, 200];
const LAST_AMOUNT_KEY = 'jgkTopUpLastAmount';
const TOPUP_STARTED_KEY = 'jgkTopUpStartedAt';
const DEFAULT_AMOUNT = 10;
const DEFAULT_QR_URL =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1677560946/qr_test_8wMaHU4F90CO2ME6oo_x7krc7.png';

export const resolveJgkTopUpPresetAmount = (amount, fallback = DEFAULT_AMOUNT) => {
  const parsed = Number(amount);
  if (Number.isFinite(parsed) && parsed > 0) {
    return (
      PRESET_AMOUNTS.find((preset) => preset >= parsed) ||
      PRESET_AMOUNTS[PRESET_AMOUNTS.length - 1]
    );
  }
  const fallbackParsed = Number(fallback);
  if (PRESET_AMOUNTS.includes(fallbackParsed)) return fallbackParsed;
  return DEFAULT_AMOUNT;
};

const formatUsdAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return '$0.00';
  return `$${numeric.toFixed(2)}`;
};

export default function BuyJGKToken({
  onCheckout,
  onCheckoutStart,
  onCheckoutError,
  presetAmount,
  returnTo,
  fundingContext = null,
  checkoutLabel,
}) {
  const { player } = useContext(AuthContext);
  const { width } = useContext(ViewPortContext);
  const { jgkPerUsd } = getLocalOpsConfig();
  const rate = Number.isFinite(jgkPerUsd) && jgkPerUsd > 0 ? jgkPerUsd : 1000;
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const checkoutInFlightRef = useRef(false);
  const [selectedAmount, setSelectedAmount] = useState(() => {
    if (Number.isFinite(presetAmount) && presetAmount > 0) {
      return resolveJgkTopUpPresetAmount(presetAmount);
    }
    const stored = Number(localStorage.getItem(LAST_AMOUNT_KEY));
    return resolveJgkTopUpPresetAmount(stored);
  });
  const [lastUsed, setLastUsed] = useState(() => {
    if (Number.isFinite(presetAmount) && presetAmount > 0) {
      return resolveJgkTopUpPresetAmount(presetAmount);
    }
    const stored = Number(localStorage.getItem(LAST_AMOUNT_KEY));
    return resolveJgkTopUpPresetAmount(stored);
  });

  const stripeQrUrl =
    process.env.REACT_APP_STRIPE_QR_URL || DEFAULT_QR_URL;
  const stripeMode = process.env.REACT_APP_STRIPE_MODE || 'prod';
  const stripeBadge =
    process.env.REACT_APP_STRIPE_BADGE_URL ||
    'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1661538167/Images/Powered_By_Stripe_nmufch.png';

  const displayAmount = useMemo(() => {
    if (Number.isFinite(selectedAmount) && selectedAmount > 0) {
      return selectedAmount;
    }
    return DEFAULT_AMOUNT;
  }, [selectedAmount]);
  const displayJgkValue = useMemo(
    () => Math.round(displayAmount * rate),
    [displayAmount, rate],
  );
  const displayJgkLabel = useMemo(
    () => `${displayJgkValue.toLocaleString()} JGK`,
    [displayJgkValue],
  );
  const isRideFunding =
    fundingContext?.type === COLLISION_SPECIALIST_RIDE_TEMPLATE;
  const isLocalOpsFunding = !!fundingContext?.type;
  const requiredTopUpJGK = useMemo(() => {
    const parsed = Number(fundingContext?.requiredTopUpJGK);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed);
  }, [fundingContext]);
  const requiredTopUpUsdLabel = useMemo(
    () =>
      requiredTopUpJGK
        ? formatUsdAmount(requiredTopUpJGK / rate)
        : null,
    [requiredTopUpJGK, rate],
  );
  const selectedTopUpPairLabel = useMemo(
    () => formatUsdJgkPairFromJgk(displayJgkValue, rate),
    [displayJgkValue, rate],
  );
  const showFundingContext = isLocalOpsFunding && !!requiredTopUpJGK;
  const showRideExtraJgkNotice =
    showFundingContext && displayJgkValue > requiredTopUpJGK;
  const showFundingShortfallNotice =
    showFundingContext && displayJgkValue < requiredTopUpJGK;
  const showGenericBalanceNote = !showFundingContext;

  const handleSelectAmount = (amount) => {
    setSelectedAmount(amount);
    setLastUsed(amount);
    localStorage.setItem(LAST_AMOUNT_KEY, String(amount));
  };

  useEffect(() => {
    if (!Number.isFinite(presetAmount) || presetAmount <= 0) return;
    const resolvedPreset = resolveJgkTopUpPresetAmount(presetAmount);
    setSelectedAmount(resolvedPreset);
    setLastUsed(resolvedPreset);
    localStorage.setItem(LAST_AMOUNT_KEY, String(resolvedPreset));
  }, [presetAmount]);

  const sanitizeReturnTo = () => {
    const fallback =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search || ''}`
        : '/jgktoken';
    const raw =
      typeof returnTo === 'string' && returnTo.trim()
        ? returnTo.trim()
        : fallback;
    try {
      const url = new URL(raw, 'http://placeholder');
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (err) {
      return fallback;
    }
  };

  const buildReturnTo = () => {
    const sanitized = sanitizeReturnTo();
    try {
      const url = new URL(sanitized, 'http://placeholder');
      // Hint for checkout return; backend will overwrite with success/cancel.
      url.searchParams.set('checkout', 'start');
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (err) {
      return sanitized;
    }
  };

  const getCheckoutErrorMessage = (err) => {
    const responseMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message;
    if (responseMessage && typeof responseMessage === 'string') {
      return responseMessage;
    }
    return 'Could not open Stripe checkout. Please try again.';
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (checkoutInFlightRef.current || isLoading) return;
    if (!player?.token) {
      const error = new Error('Please sign in again before adding JGK.');
      setCheckoutError(error.message);
      onCheckoutError?.(error);
      return;
    }
    checkoutInFlightRef.current = true;
    setCheckoutError('');
    setIsLoading(true);
    const returnTo = buildReturnTo();
    onCheckoutStart?.({ amountPreset: displayAmount, returnTo });
    try {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[jgk-topup] creating checkout session', {
          amountPreset: displayAmount,
          stripeMode,
          returnTo,
        });
      }
      const res2 = await axiosConn.post(
        'jgkTokenTransactions/create-jgktransaction-checkout-session/',
        {
          amountPreset: displayAmount,
          stripeMode,
          returnTo,
        },
        {
          headers: {
            Authorization: `Bearer ${player.token}`,
          },
        },
      );
      const sessionUrl = res2?.data?.session?.url || res2?.data?.url;
      if (!sessionUrl) {
        throw new Error('Stripe checkout did not return a session URL.');
      }
      if (typeof window !== 'undefined') {
        localStorage.setItem(TOPUP_STARTED_KEY, String(Date.now()));
        localStorage.setItem('jgkTopUpReturnTo', returnTo);
      }
      onCheckout?.({ amountPreset: displayAmount, returnTo, sessionUrl });
      window.location.href = sessionUrl;
      setIsLoading(false);
    } catch (err) {
      const message = getCheckoutErrorMessage(err);
      // eslint-disable-next-line no-console
      console.error('[jgk-topup] checkout session failed', err);
      checkoutInFlightRef.current = false;
      setIsLoading(false);
      setCheckoutError(message);
      onCheckoutError?.(err);
    }
  };

  return (
    <div className="jgk-topup">
      <div className="jgk-topup__card">
        <div className="jgk-topup__header">
          <div>
            <div className="jgk-topup__title">
              {showFundingContext
                ? isRideFunding
                  ? 'Ride request funding'
                  : 'LocalOps funding'
                : 'Top Up JGK Balance'}
            </div>
            <div className="jgk-topup__subtitle">
              {showFundingContext
                ? 'Stripe checkout adds JGK for this LocalOps action.'
                : 'Add JGK securely with Stripe.'}
            </div>
          </div>
          <img className="jgk-topup__stripe" src={stripeBadge} alt="Stripe" />
        </div>
        {showFundingContext ? (
          <div
            className="jgk-topup__funding-context"
            data-testid="ride-topup-funding-context"
          >
            <div>
              {requiredTopUpUsdLabel} is needed to fund this{' '}
              {isRideFunding ? 'ride request' : 'LocalOps action'}.
            </div>
            <div>
              You're adding {selectedTopUpPairLabel}.
            </div>
            {showRideExtraJgkNotice ? (
              <div data-testid="ride-topup-extra-jgk-notice">
                Any unused JGK stays in your balance for future LocalOps tasks.
              </div>
            ) : null}
            {showFundingShortfallNotice ? (
              <div data-testid="topup-shortfall-notice">
                This top-up may not cover the full amount. Add more JGK after
                checkout if needed.
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="jgk-topup__presets">
          {PRESET_AMOUNTS.map((amount) => {
            const isActive = amount === displayAmount;
            const isLast = amount === lastUsed;
            return (
              <button
                key={amount}
                type="button"
                className={`jgk-topup__preset ${
                  isActive ? 'is-active' : ''
                } ${isLast ? 'is-last' : ''}`}
                disabled={isLoading}
                onClick={() => handleSelectAmount(amount)}
              >
                {showFundingContext
                  ? formatUsdJgkPairFromJgk(amount * rate, rate)
                  : `$${amount} → ${formatJGK(amount * rate)}`}
                {isLast ? <span className="jgk-topup__tag">Last</span> : null}
              </button>
            );
          })}
        </div>
        {showGenericBalanceNote ? (
          <div className="jgk-topup__balance-note">
            Any unused JGK stays in your balance for future LocalOps tasks.
          </div>
        ) : null}
        <form className="jgk-topup__actions" onSubmit={submitHandler}>
          <button
            className="jgk-topup__cta"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress color="inherit" size="20px" />
            ) : (
              checkoutLabel ||
                (showFundingContext
                  ? `Add ${selectedTopUpPairLabel}`
                  : `Add ${displayJgkLabel}`)
            )}
          </button>
        </form>
        {checkoutError ? (
          <div className="jgk-topup__error" role="alert">
            {checkoutError}
          </div>
        ) : null}
        {/*
        {stripeQrUrl ? (
          <div className="jgk-topup__qr">
            <img className="jgk-topup__qr-img" src={stripeQrUrl} alt="" />
            <div className="jgk-topup__qr-label">
              Scan to checkout on mobile
            </div>
          </div>
        ) : null}
        */}
        {width < 540 && stripeBadge ? (
          <div className="jgk-topup__stripe-mobile">
            <img className="jgk-topup__stripe" src={stripeBadge} alt="Stripe" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
